import React, { useState, useEffect } from "react";
import { useTranslation } from 'react-i18next';
import { Alert, Box, CircularProgress } from '@mui/material';
import PrimaryTextField from "./PrimaryTextField/PrimaryTextField";
import PrimaryButton from './Button/PrimaryButton.js';
import CheckBox from './CheckBox/CheckBox.js';
import { useAuth } from '../Hooks/useAuth';

// Simple email validation
const validateEmail = (email) => {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
};

const Registracija = ({ setRegistrated, setTab }) => {
    const { t } = useTranslation();
    const { registerUser, loading } = useAuth();
    
    // Form state
    const [email, setEmail] = useState('');
    const [name, setName] = useState('');
    const [imone, setImone] = useState('');
    const [tel, setTel] = useState('');
    const [password, setPassword] = useState('');
    
    // Agreement state
    const [isCompanyRep, setIsCompanyRep] = useState(false);
    const [acceptedTerms, setAcceptedTerms] = useState(false);
    
    // Validation states
    const [formValid, setFormValid] = useState(false);
    const [checkerr, setCheckerr] = useState('');
    const [check2err, setCheck2err] = useState('');
    const [registrationError, setRegistrationError] = useState('');
    
    // Validate form on data change
    useEffect(() => {
        const isValid = 
            email && validateEmail(email) && 
            name && name.length >= 3 && 
            imone && imone.length >= 3 && 
            tel && tel.length >= 7 &&
            password && password.length >= 6 &&
            isCompanyRep && 
            acceptedTerms;
        
        setFormValid(isValid);
    }, [email, name, imone, tel, password, isCompanyRep, acceptedTerms]);
    
    // Clear agreement errors when they're checked
    useEffect(() => {
        if (isCompanyRep && checkerr) {
            setCheckerr('');
        }
        if (acceptedTerms && check2err) {
            setCheck2err('');
        }
    }, [isCompanyRep, acceptedTerms, checkerr, check2err]);
    
    // Handle registration
    const handleRegistration = async () => {
        try {
            // Reset errors
            setRegistrationError('');
            
            // Validate agreements first
            if (!isCompanyRep) {
                setCheckerr('error');
            }
            if (!acceptedTerms) {
                setCheck2err('error');
            }
            
            if (!isCompanyRep || !acceptedTerms) {
                return;
            }
            
            // Parse name into first name and last name
            const nameParts = name.trim().split(' ');
            const firstName = nameParts[0] || '';
            const lastName = nameParts.slice(1).join(' ') || '';
            
            // Prepare user data for registration - simplified
            const userData = {
                email: email,
                password: password,
                imonesPavadinimas: imone,
                username: imone,
                tel: tel,
                vardas: firstName,
                pavarde: lastName,
            };
            
            // Register user
            const result = await registerUser(userData);
            
            if (result.success) {
                setRegistrated(true);
                setTab(0);
            } else {
                // Check for specific error types
                if (result.error?.message?.includes('User already registered')) {
                    setRegistrationError(t('user_exists'));
                } else if (result.error?.message?.includes('duplicate key value violates unique constraint')) {
                    setRegistrationError(t('user_exists'));
                } else {
                    setRegistrationError(result.error?.message || t('registracijos_klaida'));
                }
            }
        } catch (error) {
            setRegistrationError(error.message || t('sistemos_klaida'));
        }
    };
    
    return (
        <Box sx={{ p: 2 }}>
            {/* Form fields */}
            <PrimaryTextField
                label='login_email'
                onChange={setEmail}
                fullWidth={true}
                size='small'
                classNameForm='txtFielfContainer reg'
                autoComplete="email"
                validate='email'
                errorMsg={t('neteisingasEmail')}
                required
            />
            
            <PrimaryTextField
                label='vardas_pavarde'
                onChange={setName}
                fullWidth={true}
                size='small'
                classNameForm='txtFielfContainer reg'
                autoComplete="name"
                validate='name'
                errorMsg={t('ivestiName')}
                required
            />
            
            <PrimaryTextField
                label='imones_pav'
                onChange={setImone}
                fullWidth={true}
                size='small'
                classNameForm='txtFielfContainer reg'
                autoComplete="organization"
                validate='username'
                errorMsg={t('iveskiteImoneMin')}
                required
            />
            
            <PrimaryTextField
                label='telNr'
                onChange={setTel}
                fullWidth={true}
                size='small'
                classNameForm='txtFielfContainer reg'
                autoComplete="tel"
                validate='numberValidation'
                errorMsg={t('neteisingasTel')}
                required
            />
            
            <PrimaryTextField
                label='login_pass'
                onChange={setPassword}
                type='password'
                fullWidth={true}
                size='small'
                classNameForm='txtFielfContainer reg'
                autoComplete="new-password"
                validate='password'
                errorMsg={t('iveskitePass')}
                required
            />
            
            {/* Checkboxes */}
            <div className='checkBoxContainer'>
                <CheckBox
                    label='esu_organizacijos_atstovas'
                    onClick={() => setIsCompanyRep(!isCompanyRep)}
                    className='checkAlign'
                    error={checkerr}
                    checked={isCompanyRep}
                />
                
                <CheckBox
                    text='susipazinau_su_taisyklem'
                    href="/PrivatumoPolitikaIrTaisykles"
                    target='_blank'
                    onClick={() => setAcceptedTerms(!acceptedTerms)}
                    className='checkAlign'
                    error={check2err}
                    checked={acceptedTerms}
                />
            </div>
            
            {/* Registration error message */}
            {registrationError && (
                <Alert severity="error" sx={{ mt: 2, mb: 2 }}>
                    {registrationError}
                </Alert>
            )}
            
            {/* Submit button */}
            <div className='submitButtonCont'>
                <PrimaryButton
                    disabled={!formValid || loading}
                    onClick={handleRegistration}
                    className='mobLogIn'
                    text={loading ? 
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            {t('registruojama')}
                            <CircularProgress size={20} sx={{ ml: 1 }} color="inherit" />
                        </Box> : 
                        t('registruotis')
                    }
                />
            </div>
        </Box>
    );
};

export default Registracija;